import React, {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {authorize} from "../../services/authService";
import {useNavigate} from 'react-router-dom';
import { PDFStatusContext } from '../../context/PDFStatusContext';
import { getFileSign } from "../../utils/indexedDB";
import {getFullName, signDocument} from "../../services/signService";
import { logAudit } from '../../utils/logAudit';
import domtoimage from 'dom-to-image';
import {freezeComputedSizes, isSafari, waitImagesReady} from "../../utils/imageUtils";
import {applySignaturePageMode} from "../../utils/signatureRequest";

const pad2 = (value) => String(value).padStart(2, '0');
const getDPR = () => Math.max(1, (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1);
const MIN_SIGNATURE_TEMPLATE_HOST_WIDTH_PX = 520;
const MAX_SIGNATURE_TEMPLATE_HOST_WIDTH_PX = 1800;

const formatDateTimeWithTZ = (date) => {
    const day = pad2(date.getDate());
    const month = pad2(date.getMonth() + 1);
    const year = date.getFullYear();
    const hh = pad2(date.getHours());
    const mm = pad2(date.getMinutes());
    const ss = pad2(date.getSeconds());
    const offsetMin = -date.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const offH = pad2(Math.floor(abs / 60));
    const offM = pad2(abs % 60);
    return `${day}/${month}/${year} ${hh}:${mm}:${ss} ${sign}${offH}'${offM}'`;
};

async function waitFontsAndImages(root) {
    try {
        if (document.fonts?.ready) await document.fonts.ready;
    } catch {}
    const imgs = Array.from(root?.querySelectorAll('img') || []);
    await Promise.all(imgs.map(i => i.decode?.().catch(() => null)));
}

async function captureNodeToPNG(node) {
    await waitFontsAndImages(node);
    await waitImagesReady(node);
    const rect = node.getBoundingClientRect();
    const dpr = getDPR();
    const restore = freezeComputedSizes(node);
    try {
        const doCapture = () => domtoimage.toPng(node, {
            bgcolor: null,
            width: Math.round(rect.width * dpr),
            height: Math.round(rect.height * dpr),
            style: {
                transform: `scale(${dpr})`,
                transformOrigin: 'top left',
                width: `${Math.round(rect.width)}px`,
                height: `${Math.round(rect.height)}px`,
            }
        });
        if (isSafari()) {
            try { await doCapture(); } catch {}
            await new Promise(r => setTimeout(r, 150));
        }
        return await doCapture();
    } finally {
        restore();
    }
}

async function renderHtmlTemplateToPNG(htmlTemplate, hostWidthPx = 0) {
    if (typeof htmlTemplate !== 'string' || !htmlTemplate.trim()) return null;

    const widthNum = Number(hostWidthPx);
    const baseWidth = Number.isFinite(widthNum) && widthNum > 0 ? Math.round(widthNum) : 420;
    const hostWidth = Math.max(MIN_SIGNATURE_TEMPLATE_HOST_WIDTH_PX, baseWidth);

    const mount = document.createElement('div');
    mount.style.position = 'fixed';
    mount.style.left = '-10000px';
    mount.style.top = '0';
    mount.style.pointerEvents = 'none';
    mount.style.zIndex = '-1';
    mount.style.background = '#ffffff';
    mount.style.width = `${hostWidth}px`;
    mount.innerHTML = `
      <div class="a-view">
        <div class="appearance-html-preview">
          <div class="appearance-html-preview__inner">${htmlTemplate}</div>
        </div>
      </div>
    `;
    document.body.appendChild(mount);

    try {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        const target = mount.querySelector('.appearance-html-preview__inner') || mount.firstElementChild || mount;
        if (!target) return null;
        const preferredWidth = Math.ceil(Number(target.scrollWidth) || Number(target.getBoundingClientRect().width) || hostWidth);
        if (preferredWidth > hostWidth) {
            const expandedWidth = Math.min(MAX_SIGNATURE_TEMPLATE_HOST_WIDTH_PX, preferredWidth + 8);
            mount.style.width = `${expandedWidth}px`;
            await new Promise((resolve) => requestAnimationFrame(resolve));
        }
        return await captureNodeToPNG(target);
    } finally {
        mount.remove();
    }
}

async function getImageDimensionsFromDataUrl(dataUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({
            width: Number(img.naturalWidth || img.width || 0),
            height: Number(img.naturalHeight || img.height || 0),
        });
        img.onerror = reject;
        img.src = dataUrl;
    });
}

const refreshHtmlTemplateDate = (htmlTemplate) => {
    if (typeof htmlTemplate !== 'string' || !htmlTemplate.includes('data-appearance-date')) {
        return { hasDateNode: false, htmlTemplate, nowIso: null };
    }
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlTemplate, 'text/html');
        const dateNodes = doc.querySelectorAll('[data-appearance-date]');
        if (!dateNodes.length) {
            return { hasDateNode: false, htmlTemplate, nowIso: null };
        }
        const nameLineNodes = doc.querySelectorAll('[class*="nameLine"]');
        nameLineNodes.forEach((node) => {
            node.style.whiteSpace = 'nowrap';
            node.style.wordBreak = 'normal';
            node.style.overflowWrap = 'normal';
        });
        const now = new Date();
        const nextDate = formatDateTimeWithTZ(now);
        dateNodes.forEach((node) => {
            node.textContent = nextDate;
        });
        return {
            hasDateNode: true,
            htmlTemplate: doc.body.innerHTML,
            nowIso: now.toISOString()
        };
    } catch {
        return { hasDateNode: false, htmlTemplate, nowIso: null };
    }
};

export const ComponentAsideDefault = ({ modalDefinirRef }) => {
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const { t } = useTranslation();
    const [signAuthRequired, setSignAuthRequired] = useState(false);
    const {
        viewerLoaded,
        documentLoaded,
        draggableReady,
        pdfNotProtected
    } = useContext(PDFStatusContext);
    const [showError, setShowError] = useState(false);
    const timeoutRef = useRef(null);
    const canSign = viewerLoaded && documentLoaded && draggableReady && pdfNotProtected;
    const isBrowserBlocked = (typeof window !== 'undefined') && (sessionStorage.getItem('browserBlocked') === '1');
    const isDocumentBlocked = !pdfNotProtected;
    const isBlocked = isBrowserBlocked || isDocumentBlocked;

    useEffect(() => {
        if (pdfNotProtected === false) {
            try {
                logAudit({
                    state: 'WARN',
                    message: 'Acciones de firma deshabilitadas por documento protegido',
                    event: 'SIGN_ACTIONS_DISABLED_PROTECTED_DOC',
                    args: {viewerLoaded, documentLoaded, draggableReady}
                });
            } catch {
            }
        }
    }, [pdfNotProtected, viewerLoaded, documentLoaded, draggableReady]);


    useEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        if (isBlocked) {
            return;
        }

        if (canSign) {
            return;
        }
        timeoutRef.current = setTimeout(() => {
            // Solo muestra el error si todavía no puede firmar
            if (!canSign) {
                try {
                    logAudit({
                      state: 'ERROR',
                      error: 'Timeout esperando que el visor/documento esté listo para firmar',
                      event: 'PDF_VIEWER_TIMEOUT',
                      args: { viewerLoaded, documentLoaded, draggableReady, pdfNotProtected }
                    });
                } catch {}
                navigate('/signer/signDocument', {
                    state: {
                        alertType: 'error',
                        alertMessageKey: 'componentAlerts.viewerError'
                    }
                });
            }
        }, 45000);

        // Limpia al desmontar
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [canSign,  isBlocked, navigate]);


    const getConfig = () => {
        const configEncoded = sessionStorage.getItem('configs');
        if (configEncoded) {
            const configDecoded = atob(configEncoded); // Decodificar base64
            return JSON.parse(configDecoded); // Parsear a JSON
        }
        return null; // Retorna null si no existe la configuración
    };

    const handleAuthorize = async () => {
        if (isBlocked) return;
        const config = getConfig();
        if (config && config.SIGN_AUTH_REQUIRED ) {
                sessionStorage.setItem('sentToSign', 'true');
        }
        try {
            window.location.href = await authorize(
                process.env.REACT_APP_IDPID,
                process.env.REACT_APP_RESPONSE_TYPE,
                process.env.REACT_APP_STATE,
                process.env.REACT_APP_REDIRECT_URI
            );
        } catch (error) {
            console.error('Authorization failed', error);
        }
    };

    const handleCancel = () => {
        navigate('/');
    };

    useEffect(() => {
        window.popupModal();
    }, [signAuthRequired]);

    useEffect(() => {
        if (sessionStorage.getItem('configs')) {
            const configs = JSON.parse(atob(sessionStorage.getItem('configs')));
            setSignAuthRequired(configs.SIGN_AUTH_REQUIRED);
        }
    }, []);

    function createHiddenAppearanceFromStorage() {
        const html = sessionStorage.getItem('signatureAppearanceHtml');
        if (!html) return null;

        const wrapper = document.createElement('div');
        wrapper.style.position = 'fixed';
        wrapper.style.left = '-99999px';
        wrapper.style.top = '0';
        wrapper.style.background = '#fff';
        wrapper.innerHTML = html;

        document.body.appendChild(wrapper);
        return wrapper;
    }

    const handleSign = async () => {
        if (isBlocked) return;
        try {
            const fileName = sessionStorage.getItem('fileName');
            const pdfFile = await getFileSign(fileName);
            const storedAppearance = sessionStorage.getItem('appearance');
            let appearanceData = {};
            try { appearanceData = storedAppearance ? JSON.parse(storedAppearance) : {}; } catch { appearanceData = {}; }

            const htmlTemplateStored = sessionStorage.getItem('signatureAppearanceHtml');
            const refreshedTemplate = refreshHtmlTemplateDate(htmlTemplateStored);
            if (refreshedTemplate.hasDateNode) {
                sessionStorage.setItem('signatureAppearanceHtml', refreshedTemplate.htmlTemplate);
                appearanceData.text = appearanceData.text || {};
                if (refreshedTemplate.nowIso) {
                    appearanceData.text.dateSelectedAtIso = refreshedTemplate.nowIso;
                }
                const width = Number(appearanceData?.width);
                const hostWidth = Number.isFinite(width) && width > 0 ? Math.round(width / 0.9) : 420;
                try {
                    const regenerated = await renderHtmlTemplateToPNG(refreshedTemplate.htmlTemplate, hostWidth);
                    if (regenerated) {
                        sessionStorage.setItem('signatureAppearance', regenerated);
                        appearanceData.image = appearanceData.image || {};
                        appearanceData.image.value = regenerated;
                        try {
                            const regeneratedDims = await getImageDimensionsFromDataUrl(regenerated);
                            if (regeneratedDims.width > 0 && regeneratedDims.height > 0) {
                                appearanceData.baseW = regeneratedDims.width;
                                appearanceData.baseH = regeneratedDims.height;
                                appearanceData.imageAspect = regeneratedDims.width / regeneratedDims.height;
                            }
                        } catch (dimErr) {
                            console.warn('No se pudo leer tamaño del PNG regenerado.', dimErr);
                        }
                    }
                } catch (err) {
                    console.error('No se pudo regenerar signatureAppearance antes de firmar.', err);
                }
                sessionStorage.setItem('appearance', JSON.stringify(appearanceData));
            }

            // --- Read original frame (rotated space) and size fallbacks (keep your logic) ---
            const lowerLeft  = appearanceData?.location?.frame?.lowerLeft;
            const upperRight = appearanceData?.location?.frame?.upperRight;

            if (!lowerLeft || lowerLeft.x == null || lowerLeft.y == null) {
                throw new Error('Invalid appearance: missing lowerLeft coordinates');
            }
            let width  = Number(appearanceData?.pdfWidth);
            let height = Number(appearanceData?.pdfHeight);
            // Fallback: derive from upperRight if available
            if (!Number.isFinite(width) || !Number.isFinite(height)) {
                if (upperRight?.x != null && upperRight?.y != null && lowerLeft) {
                    width  = Math.abs(upperRight.x - lowerLeft.x);
                    height = Math.abs(upperRight.y - lowerLeft.y);
                }
            }

// Final guard: avoid NaN/undefined leaking to API
            width  = Number.isFinite(width)  ? width  : 0;
            height = Number.isFinite(height) ? height : 0;
            // --- Metadata from viewer (added earlier in viewer.mjs) ---
            const pageRotation = Number(appearanceData.pageRotation || 0);                // 0|90|180|270
            const pdfWidth0    = Number(appearanceData.pdfWidth0  || appearanceData.pdfWidth  || 0);
            const pdfHeight0   = Number(appearanceData.pdfHeight0 || appearanceData.pdfHeight || 0);
            const cropToMediaOffset = appearanceData.cropToMediaOffset || { dx: 0, dy: 0 };

            // --- Helpers (scoped here to keep the change minimal) ---
            function normalizePointToPdfBase(x, y) {
                switch (pageRotation) {
                    case 0:   return { x, y };
                    case 90:  return { x: y,              y: pdfWidth0  - x };
                    case 180: return { x: pdfWidth0 - x,  y: pdfHeight0 - y };
                    case 270: return { x: pdfHeight0 - y, y: x };
                    default:  return { x, y };
                }
            }
            function normalizeRectForPdfBase(rect) {
                const ll = rect.lowerLeft;
                const ur = rect.upperRight ?? { x: ll.x + width, y: ll.y + height }; // fallback from size if UR missing
                const pA = normalizePointToPdfBase(Number(ll.x), Number(ll.y));
                const pB = normalizePointToPdfBase(Number(ur.x), Number(ur.y));

                const nx1 = Math.min(pA.x, pB.x), ny1 = Math.min(pA.y, pB.y);
                const nx2 = Math.max(pA.x, pB.x), ny2 = Math.max(pA.y, pB.y);

                const dx = Number(cropToMediaOffset.dx) || 0;
                const dy = Number(cropToMediaOffset.dy) || 0;

                const round = v => Math.round(v * 1000) / 1000;
                const llN = { x: round(nx1 + dx), y: round(ny1 + dy) };
                const urN = { x: round(nx2 + dx), y: round(ny2 + dy) };
                return {
                    lowerLeft:  llN,
                    upperRight: urN,
                    width:  round(urN.x - llN.x),
                    height: round(urN.y - llN.y),
                };
            }

            // --- Normalize frame to base PDF space (rotation = 0) ---
            const normalized = normalizeRectForPdfBase({
                lowerLeft:  { x: lowerLeft.x, y: lowerLeft.y },
                upperRight: upperRight && upperRight.x != null && upperRight.y != null ? { x: upperRight.x, y: upperRight.y } : undefined
            });

            // Use normalized values for signer payload
             const x = normalized.lowerLeft.x;
             let y = normalized.lowerLeft.y;
            let nWidth  = normalized.width;
            let nHeight = normalized.height;
            // 🔹 Corrige proporción vertical según el aspecto real del PNG (baseW/baseH)
            try {
                const aspect = (appearanceData?.baseW > 0 && appearanceData?.baseH > 0)
                ? (appearanceData.baseW / appearanceData.baseH)
                : (appearanceData?.imageAspect || 0);
                if (aspect > 0) {
                    const idealH = nWidth / aspect;
                    // Corrige solo si hay una diferencia >5%
                    if (Math.abs(idealH - nHeight) / idealH > 0.05) {
                        console.warn(`Corrigiendo altura PDF de ${nHeight} → ${idealH}`);
                        // Recalcular altura manteniendo el centro original
                       const deltaH = idealH - nHeight;
                        nHeight = idealH;
                        y = y - deltaH /2; // desplaza hacia arriba la mitad del cambio
                    }
                }
            } catch (e) {
                console.warn('No se pudo recalcular altura proporcional', e);
            }
            // Final guard
            nWidth  = Number.isFinite(nWidth)  ? nWidth  : 0;
            nHeight = Number.isFinite(nHeight) ? nHeight : 0;

            const pageNumber = appearanceData?.location?.page ?? "last";
            let base64Image = appearanceData?.image?.value;

            if (!base64Image) {
                const sigFromLS = sessionStorage.getItem('signatureAppearance');
                base64Image = (typeof sigFromLS === 'string') ? sigFromLS : null;
            }

            if (base64Image) {
                base64Image = base64Image
                    .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
                    .trim();

                if (base64Image === '') {
                    base64Image = null;
                }
            }

            const appearance = {
                signature_details: {
                    details: [{
                        type: "text",
                        value: " "
                    }]
                }
            };

            if (base64Image) {
                appearance.background_image = {
                    binary: base64Image
                };
            }

            const requestJson = applySignaturePageMode({
                "signature_field": {
                    "location": {
                        "page": {
                            "number": pageNumber
                        },
                        "rectangle": {
                            "x": x,
                            "y": y,
                            "height": nHeight,
                            "width": nWidth
                        }
                    },
                    "appearance": appearance
                }
            });

            console.log ('Request JSON for signing:', requestJson);
            const response = await signDocument(sessionStorage.getItem('access_token'), pdfFile, requestJson);
            if (!response.ok) {
                const respText = await response.text();
                throw new Error(`Error al firmar: ${response.status} - ${respText}`);
            }
            const respJson = await response.json();
            if (respJson && respJson.tasks && respJson.tasks.pending?.[0]?.url) {
                window.location.href = respJson.tasks.pending[0].url;
            }
        } catch (error) {
            setError('Error al validar el PIN. Por favor, inténtelo de nuevo.');
        }
    };

    const isSignatureAppearanceModalEnabled = () => {
        try {
            const encoded = sessionStorage.getItem('configs');
            if (!encoded) return true;
            const cfg = JSON.parse(atob(encoded));
            const v = cfg?.SIGNATURE_APPEARANCE_MODAL;
            return (v === undefined ? true : !!v);
        } catch { return true; }
    };
    const selectionModalEnabled = isSignatureAppearanceModalEnabled();

    return (
        <div className="component-aside-default">
            <div className="component-actions">
                <button
                    style={{marginTop: '10px'}}
                    type="button"
                    data-modal={selectionModalEnabled ? 'modalSeleccionarApariencia' : 'modalDefinirApariencia'}
                    className="btn-medium btn-primary-outline modal-trigger"
                    disabled={isBlocked}
                >
                    {t('componentAsideDefault.configurarFirma')}
                </button>
            </div>
            <div className="component-actions">
                {signAuthRequired && <button
                    style={{marginTop: '10px'}}
                    type="button"
                    onClick={handleSign}
                    className="btn-medium btn-primary"
                    disabled={isBlocked || !canSign}
                >{t('componentAsideDefault.sign')}
                </button>}
                {!signAuthRequired && <button
                    style={{marginTop: '10px'}}
                    type="button"
                    className="btn-medium btn-primary"
                    onClick={handleAuthorize}
                    disabled={isBlocked || !canSign}
                >{t('componentAsideDefault.sign')}
                </button>}
                <button
                    style={{marginTop: '10px'}}
                    type="button"
                    className="btn-medium btn-secondary"
                    onClick={handleCancel}
                >
                    {t('componentAsideDefault.cancel')}
                </button>
            </div>

        </div>
    );
};
